import { useRef, useState } from 'react';

// Only drags that carry files from the OS count; in-page drags (reordering
// thumbnails, answer chips) pass through untouched.
const carriesFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');

// Turns any element into a drop target for files. Spread `dropProps` on the
// element and style it with `isDragOver`. `onFiles` gets an array of File.
const useFileDrop = (onFiles, { disabled = false } = {}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  // dragenter/dragleave fire for every child crossed; count them so the
  // highlight does not flicker while the pointer moves over nested elements.
  const depthRef = useRef(0);

  const reset = () => {
    depthRef.current = 0;
    setIsDragOver(false);
  };

  const dropProps = {
    onDragEnter: (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      depthRef.current += 1;
      if (!disabled) setIsDragOver(true);
    },
    onDragOver: (e) => {
      if (!carriesFiles(e)) return;
      // Without preventDefault the browser would open the file in the tab.
      e.preventDefault();
      e.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
    },
    onDragLeave: (e) => {
      if (!carriesFiles(e)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsDragOver(false);
    },
    onDrop: (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      reset();
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) onFiles(files);
    },
  };

  return { isDragOver, dropProps };
};

export default useFileDrop;
